import type { AxiosRequestConfig } from "axios";

import { http } from "@/api/http";
import { ApiError } from "@/types/api";
import {
  ADMIN_PAGE_SIZES,
  type AdminApplication,
  type AdminApplicationStatus,
  type AdminApplicationTrackingStatus,
  type AdminCommercialItem,
  type AdminCommercialKind,
  type AdminCommercialStatus,
  type AdminCountMap,
  type AdminListQuery,
  type AdminMentorBooking,
  type AdminMentorBookingStatus,
  type AdminOverview,
  type AdminPage,
  type AdminSubscription,
  type AdminSubscriptionStatus,
  type AdminSupportChannel,
  type AdminSupportPriority,
  type AdminSupportRequest,
  type AdminSupportStatus,
  type AdminUser,
} from "@/types/admin";

export type {
  AdminApplication,
  AdminCommercialItem,
  AdminListQuery,
  AdminMentorBooking,
  AdminOverview,
  AdminPage,
  AdminSubscription,
  AdminSupportRequest,
  AdminUser,
} from "@/types/admin";

const INVALID_RESPONSE_CODE = "invalid_admin_response";
const INVALID_QUERY_CODE = "invalid_admin_query";

const APPLICATION_STATUSES: ReadonlySet<string> = new Set([
  "draft_created",
  "queued",
  "running",
  "autopilot_running",
  "manual_apply_required",
  "automation_blocked",
  "review_ready",
  "ready_for_user_submission",
  "needs_review",
  "blocked",
  "failed",
  "cancelled",
]);
const APPLICATION_TRACKING_STATUSES: ReadonlySet<string> = new Set([
  "not_applied",
  "in_progress",
  "applied_no_response",
  "rejected",
  "offer",
]);
const SUPPORT_CHANNELS: ReadonlySet<string> = new Set(["email", "inbox", "bug"]);
const SUPPORT_STATUSES: ReadonlySet<string> = new Set([
  "open",
  "in_progress",
  "waiting_for_user",
  "resolved",
  "closed",
]);
const SUPPORT_PRIORITIES: ReadonlySet<string> = new Set(["normal", "high", "urgent"]);
const COMMERCIAL_KINDS: ReadonlySet<string> = new Set(["subscription", "mentor_booking"]);
const SUBSCRIPTION_STATUSES: ReadonlySet<string> = new Set([
  "free",
  "trialing",
  "active",
  "past_due",
  "scheduled_to_cancel",
  "paused",
  "incomplete",
  "incomplete_expired",
  "canceled",
  "unpaid",
]);
const MENTOR_BOOKING_STATUSES: ReadonlySet<string> = new Set([
  "payment_pending",
  "paid",
  "confirmed",
  "meeting_setup_pending",
  "checkout_failed",
  "expired",
  "cancelled",
]);

type JsonRecord = Record<string, unknown>;
type QueryKey = "page" | "page_size" | "priority" | "search" | "status" | "kind";

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(
  value: JsonRecord,
  required: readonly string[],
  optional: readonly string[] = [],
): boolean {
  const allowed = new Set([...required, ...optional]);
  return required.every((key) => Object.prototype.hasOwnProperty.call(value, key))
    && Reflect.ownKeys(value).every((key) => typeof key === "string" && allowed.has(key));
}

function invalidContract(): never {
  throw new Error("invalid administration response contract");
}

function requiredString(value: unknown): string {
  if (typeof value !== "string") invalidContract();
  return value;
}

function requiredNullableString(value: unknown): string | null {
  if (value === null) return null;
  return requiredString(value);
}

function requiredBoolean(value: unknown): boolean {
  if (typeof value !== "boolean") invalidContract();
  return value;
}

function requiredCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    invalidContract();
  }
  return value;
}

function requiredPageNumber(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 1) {
    invalidContract();
  }
  return value;
}

function requiredTimestamp(value: unknown): string {
  const timestamp = requiredString(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/.exec(timestamp);
  if (!match) invalidContract();

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  if (year < 1 || month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59) {
    invalidContract();
  }
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  if (month === 2 && leapYear) daysInMonth[1] = 29;
  const maximumDay = daysInMonth[month - 1];
  if (maximumDay === undefined || day < 1 || day > maximumDay) invalidContract();

  const zone = match[8];
  if (zone === undefined) invalidContract();
  if (zone !== "Z") {
    const offset = /^(?:[+-])(\d{2}):(\d{2})$/.exec(zone);
    if (!offset || Number(offset[1]) > 23 || Number(offset[2]) > 59) invalidContract();
  }
  return timestamp;
}

function requiredNullableTimestamp(value: unknown): string | null {
  if (value === null) return null;
  return requiredTimestamp(value);
}

function requiredChoice(value: unknown, choices: ReadonlySet<string>): string {
  const candidate = requiredString(value);
  if (!choices.has(candidate)) invalidContract();
  return candidate;
}

function countMap(value: unknown, choices: ReadonlySet<string>): AdminCountMap {
  if (!isRecord(value)) invalidContract();
  if (Reflect.ownKeys(value).some((key) => typeof key !== "string" || !choices.has(key))) {
    invalidContract();
  }
  const result: AdminCountMap = {};
  for (const [key, count] of Object.entries(value)) {
    if (!choices.has(key)) invalidContract();
    result[key] = requiredCount(count);
  }
  return result;
}

function validateOverview(value: unknown): AdminOverview {
  if (!isRecord(value) || !hasOnlyKeys(value, [
    "generated_at",
    "counts",
    "application_statuses",
    "support_available",
    "support_statuses",
    "commercial_statuses",
  ])) invalidContract();
  const counts = value.counts;
  if (!isRecord(counts) || !hasOnlyKeys(counts, [
    "users_total",
    "users_active",
    "applications_total",
    "support_open",
    "subscriptions_active",
    "mentor_bookings_active",
    "jobs_running",
  ])) invalidContract();
  const supportAvailable = requiredBoolean(value.support_available);
  const supportStatuses = value.support_statuses === null
    ? null
    : countMap(value.support_statuses, SUPPORT_STATUSES);
  if (!supportAvailable && supportStatuses !== null) invalidContract();
  const commercialStatusesValue = value.commercial_statuses;
  if (!isRecord(commercialStatusesValue)) invalidContract();
  if (Reflect.ownKeys(commercialStatusesValue).some(
    (key) => typeof key !== "string" || !COMMERCIAL_KINDS.has(key),
  )) invalidContract();
  const commercialStatuses: Partial<
    Record<AdminCommercialKind, Partial<Record<AdminCommercialStatus, number>>>
  > = {};
  for (const [kind, statuses] of Object.entries(commercialStatusesValue)) {
    if (!COMMERCIAL_KINDS.has(kind)) invalidContract();
    const choices = kind === "subscription" ? SUBSCRIPTION_STATUSES : MENTOR_BOOKING_STATUSES;
    commercialStatuses[kind as AdminCommercialKind] = countMap(statuses, choices);
  }
  return {
    generated_at: requiredTimestamp(value.generated_at),
    counts: {
      users_total: requiredCount(counts.users_total),
      users_active: requiredCount(counts.users_active),
      applications_total: requiredCount(counts.applications_total),
      support_open: counts.support_open === null ? null : requiredCount(counts.support_open),
      subscriptions_active: requiredCount(counts.subscriptions_active),
      mentor_bookings_active: requiredCount(counts.mentor_bookings_active),
      jobs_running: requiredCount(counts.jobs_running),
    },
    application_statuses: countMap(
      value.application_statuses,
      new Set([...APPLICATION_STATUSES, ...APPLICATION_TRACKING_STATUSES]),
    ) as AdminOverview["application_statuses"],
    support_available: supportAvailable,
    support_statuses: supportStatuses as AdminOverview["support_statuses"],
    commercial_statuses: commercialStatuses,
  };
}

function validatePage<T>(value: unknown, validateItem: (item: unknown) => T): AdminPage<T> {
  if (!isRecord(value) || !hasOnlyKeys(value, ["items", "page", "page_size", "total", "total_pages"])) {
    invalidContract();
  }
  if (!Array.isArray(value.items)) invalidContract();
  for (let index = 0; index < value.items.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value.items, index)) invalidContract();
  }
  const pageSize = requiredPageNumber(value.page_size);
  if (!ADMIN_PAGE_SIZES.includes(pageSize as (typeof ADMIN_PAGE_SIZES)[number])) invalidContract();
  const total = requiredCount(value.total);
  const totalPages = requiredCount(value.total_pages);
  if (value.items.length > pageSize
    || totalPages !== (total === 0 ? 0 : Math.ceil(total / pageSize))) invalidContract();
  return {
    items: value.items.map(validateItem),
    page: requiredPageNumber(value.page),
    page_size: pageSize as AdminPage<T>["page_size"],
    total,
    total_pages: totalPages,
  };
}

const USER_KEYS = [
  "id",
  "username",
  "email",
  "disabled_at",
  "email_verified_at",
  "totp_enabled",
  "created_at",
  "last_seen_at",
] as const;

function validateUser(value: unknown): AdminUser {
  if (!isRecord(value) || !hasOnlyKeys(value, USER_KEYS)) invalidContract();
  return {
    id: requiredString(value.id),
    username: requiredString(value.username),
    email: requiredString(value.email),
    disabled_at: requiredNullableTimestamp(value.disabled_at),
    email_verified_at: requiredNullableTimestamp(value.email_verified_at),
    totp_enabled: requiredBoolean(value.totp_enabled),
    created_at: requiredTimestamp(value.created_at),
    last_seen_at: requiredNullableTimestamp(value.last_seen_at),
  };
}

const APPLICATION_KEYS = [
  "id",
  "user_id",
  "company",
  "role_title",
  "location",
  "status",
  "tracking_status",
  "role_status",
  "portal_type",
  "last_safe_step",
  "outcome_reason_code",
  "review_required",
  "automation_blocked",
  "created_at",
  "updated_at",
] as const;

function validateApplication(value: unknown): AdminApplication {
  if (!isRecord(value) || !hasOnlyKeys(value, APPLICATION_KEYS)) invalidContract();
  return {
    id: requiredString(value.id),
    user_id: requiredString(value.user_id),
    company: requiredString(value.company),
    role_title: requiredString(value.role_title),
    location: requiredNullableString(value.location),
    status: requiredChoice(value.status, APPLICATION_STATUSES) as AdminApplicationStatus,
    tracking_status: value.tracking_status === null
      ? null
      : requiredChoice(value.tracking_status, APPLICATION_TRACKING_STATUSES) as AdminApplicationTrackingStatus,
    role_status: requiredNullableString(value.role_status),
    portal_type: requiredNullableString(value.portal_type),
    last_safe_step: requiredNullableString(value.last_safe_step),
    outcome_reason_code: requiredNullableString(value.outcome_reason_code),
    review_required: requiredBoolean(value.review_required),
    automation_blocked: requiredBoolean(value.automation_blocked),
    created_at: requiredTimestamp(value.created_at),
    updated_at: requiredTimestamp(value.updated_at),
  };
}

const SUPPORT_KEYS = [
  "id",
  "user_id",
  "channel",
  "status",
  "priority",
  "attachment_count",
  "created_at",
  "updated_at",
  "resolved_at",
] as const;
const SUPPORT_OPTIONAL_KEYS = ["username", "email"] as const;

function validateSupportRequest(value: unknown): AdminSupportRequest {
  if (!isRecord(value) || !hasOnlyKeys(value, SUPPORT_KEYS, SUPPORT_OPTIONAL_KEYS)) invalidContract();
  const result: AdminSupportRequest = {
    id: requiredString(value.id),
    user_id: requiredString(value.user_id),
    channel: requiredChoice(value.channel, SUPPORT_CHANNELS) as AdminSupportChannel,
    status: requiredChoice(value.status, SUPPORT_STATUSES) as AdminSupportStatus,
    priority: requiredChoice(value.priority, SUPPORT_PRIORITIES) as AdminSupportPriority,
    attachment_count: requiredCount(value.attachment_count),
    created_at: requiredTimestamp(value.created_at),
    updated_at: requiredTimestamp(value.updated_at),
    resolved_at: requiredNullableTimestamp(value.resolved_at),
  };
  if (Object.prototype.hasOwnProperty.call(value, "username")) {
    result.username = requiredString(value.username);
  }
  if (Object.prototype.hasOwnProperty.call(value, "email")) {
    result.email = requiredString(value.email);
  }
  return result;
}

const COMMERCIAL_KEYS = [
  "kind",
  "user_id",
  "username",
  "id",
  "plan_code",
  "status",
  "session_type",
  "start_at",
  "end_at",
  "duration_minutes",
  "amount",
  "currency",
  "updated_at",
] as const;

function commercialBase(value: JsonRecord): {
  user_id: string;
  username: string;
  id: string;
  start_at: string | null;
  end_at: string | null;
  updated_at: string;
} {
  return {
    user_id: requiredString(value.user_id),
    username: requiredString(value.username),
    id: requiredString(value.id),
    start_at: requiredNullableTimestamp(value.start_at),
    end_at: requiredNullableTimestamp(value.end_at),
    updated_at: requiredTimestamp(value.updated_at),
  };
}

function validateCommercialItem(value: unknown): AdminCommercialItem {
  if (!isRecord(value) || !hasOnlyKeys(value, COMMERCIAL_KEYS)) invalidContract();
  const kind = requiredChoice(value.kind, COMMERCIAL_KINDS);
  if (kind === "subscription") {
    if (value.session_type !== null || value.duration_minutes !== null
      || value.amount !== null || value.currency !== null) invalidContract();
    const status = requiredChoice(value.status, SUBSCRIPTION_STATUSES) as AdminSubscriptionStatus;
    const subscription: AdminSubscription = {
      ...commercialBase(value),
      kind: "subscription",
      status,
      plan_code: requiredString(value.plan_code),
      session_type: null,
      duration_minutes: null,
      amount: null,
      currency: null,
    };
    return subscription;
  }
  if (value.plan_code !== null || value.session_type === null
    || value.duration_minutes === null || value.amount === null || value.currency === null) {
    invalidContract();
  }
  const duration = requiredCount(value.duration_minutes);
  if (duration !== 30 && duration !== 60) invalidContract();
  const amount = requiredCount(value.amount);
  if (amount <= 0) invalidContract();
  const startAt = requiredTimestamp(value.start_at);
  const endAt = requiredTimestamp(value.end_at);
  const status = requiredChoice(value.status, MENTOR_BOOKING_STATUSES) as AdminMentorBookingStatus;
  const mentorBooking: AdminMentorBooking = {
    ...commercialBase(value),
    kind: "mentor_booking",
    status,
    plan_code: null,
    session_type: requiredString(value.session_type),
    start_at: startAt,
    end_at: endAt,
    duration_minutes: duration,
    amount,
    currency: requiredString(value.currency),
  };
  return mentorBooking;
}

function invalidResponse(endpoint: string): ApiError {
  return new ApiError("The administration response was invalid.", {
    code: INVALID_RESPONSE_CODE,
    endpoint,
  });
}

function invalidQuery(endpoint: string): ApiError {
  return new ApiError("The administration query was invalid.", {
    code: INVALID_QUERY_CODE,
    endpoint,
  });
}

function queryConfig(
  endpoint: string,
  query: AdminListQuery,
  supportedKeys: readonly QueryKey[],
  signal?: AbortSignal,
): AxiosRequestConfig {
  if (!isRecord(query)) throw invalidQuery(endpoint);
  const params: Record<string, string | number> = {};
  for (const key of supportedKeys) {
    const value = query[key];
    if (value === undefined || value === null) continue;
    if (key === "page" || key === "page_size") {
      if (typeof value !== "number" || !Number.isSafeInteger(value)) throw invalidQuery(endpoint);
      params[key] = value;
      continue;
    }
    if (typeof value !== "string") throw invalidQuery(endpoint);
    const trimmed = value.trim();
    if (trimmed) params[key] = trimmed;
  }
  const config: AxiosRequestConfig = { params };
  if (signal) config.signal = signal;
  return config;
}

async function getValidated<T>(
  endpoint: string,
  config: AxiosRequestConfig,
  validate: (value: unknown) => T,
): Promise<T> {
  const response = await http.get<unknown>(endpoint, config);
  try {
    return validate(response.data);
  } catch {
    throw invalidResponse(endpoint);
  }
}

export async function getAdminOverview(signal?: AbortSignal): Promise<AdminOverview> {
  const config: AxiosRequestConfig = {};
  if (signal) config.signal = signal;
  return getValidated("/admin/overview", config, validateOverview);
}

export async function getAdminUsers(
  query: AdminListQuery = {},
  signal?: AbortSignal,
): Promise<AdminPage<AdminUser>> {
  const endpoint = "/admin/users";
  return getValidated(
    endpoint,
    queryConfig(endpoint, query, ["page", "page_size", "search", "status"], signal),
    (value) => validatePage(value, validateUser),
  );
}

export async function getAdminApplications(
  query: AdminListQuery = {},
  signal?: AbortSignal,
): Promise<AdminPage<AdminApplication>> {
  const endpoint = "/admin/applications";
  return getValidated(
    endpoint,
    queryConfig(endpoint, query, ["page", "page_size", "search", "status"], signal),
    (value) => validatePage(value, validateApplication),
  );
}

export async function getAdminSupport(
  query: AdminListQuery = {},
  signal?: AbortSignal,
): Promise<AdminPage<AdminSupportRequest>> {
  const endpoint = "/admin/support";
  return getValidated(
    endpoint,
    queryConfig(endpoint, query, ["page", "page_size", "status", "priority"], signal),
    (value) => validatePage(value, validateSupportRequest),
  );
}

export async function getAdminCommercial(
  query: AdminListQuery = {},
  signal?: AbortSignal,
): Promise<AdminPage<AdminCommercialItem>> {
  const endpoint = "/admin/commercial";
  return getValidated(
    endpoint,
    queryConfig(endpoint, query, ["page", "page_size", "search", "status", "kind"], signal),
    (value) => validatePage(value, validateCommercialItem),
  );
}
